import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Checklist, Close, Language, PictureAsPdf, WorkHistoryOutlined, WorkspacePremium } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import DescriptionIcon from "@mui/icons-material/Description";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { styled } from "@mui/material/styles";
import SendEmailModal from "../Emailing/SendEmailModal";
import EmailHistory from "../Emailing/EmailHistory";
import { COLORS } from "colors/color";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import HistoryIcon from "@mui/icons-material/History";
import { GraduationCap } from 'lucide-react';
import { Download } from "bootstrap-icons-react";
import EmailPopup from "./EmailPopup";
// import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import FilterDialog from "components/Dialog/FilterDialog";
import filterIcon from "../../assets/img/filter_icon.svg";
import searchIcon from "../../assets/img/search_icon_grey.svg";
import { PostSearchBar } from "components/searchBars/SearchBars";
import { Filter } from "lucide-react";
const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.spacing(4),
  boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
  marginBottom: theme.spacing(6),
  overflow: "hidden",
  position: "relative",
  transition: "all 0.3s ease-in-out",
  background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  "&:hover": {
    transform: "translateY(-10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    "&:hover": {
      transform: "translateY(-5px)",
    },
  },
}));

const apiUrl = process.env.REACT_APP_API_URL;

const ScoreTriangle = styled(Box)(({ theme, score }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: "0 340px 200px 0",
  borderColor: `transparent ${getScoreColor(score)} transparent transparent`,
  zIndex: 0,
  borderTopRightRadius: "25px",
  borderBottomRightRadius: "25px",
  [theme.breakpoints.down('md')]: {
    borderWidth: "0 250px 150px 0",
  },
  [theme.breakpoints.down('sm')]: {
    borderWidth: "0 180px 110px 0",
    borderTopRightRadius: "15px",
    borderBottomRightRadius: "15px",
  },
}));

const ScoreContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 15,
  right: 15,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    top: 10,
    right: 10,
  },
}));

const InfoIcon = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: -10,
  right: -10,
  padding: 4,
  color: "#fff",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  [theme.breakpoints.down('sm')]: {
    top: -8,
    right: -8,
    padding: 2,
  },
  zIndex: 2,
}));

const ScoreText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "3rem",
  color: "#fff",
  [theme.breakpoints.down('md')]: {
    fontSize: "2.5rem",
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: "1.8rem",
  },
}));

const ScoreComment = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "1rem",
  color: "#fff",
  textAlign: "right",
  maxWidth: "180px",
  [theme.breakpoints.down('md')]: {
    fontSize: "0.9rem",
    maxWidth: "150px",
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: "0.75rem",
    maxWidth: "100px",
  },
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 500,
  borderRadius: "20px",
  boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  "& .MuiChip-icon": {
    color: theme.palette.text.secondary,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: "0.75rem",
    height: "28px",
    margin: theme.spacing(0.25),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: "25px",
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: "none",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1, 2),
    fontSize: "0.875rem",
  },
}));

const getScoreColor = (score) => {
  if (score >= 75) return "#4caf50";
  if (score >= 45) return "#0befef";
  return "#f9510e";
};

const getScoreComment = (score) => {
  if (score >= 75) return "Excellent match!";
  if (score >= 45) return "Good potential";
  return "Consider carefully";
};

const StyledTypography = styled(Typography)(() => ({
  fontFamily: "Poppins",
}));

const CandidateCard = ({ candidate, post_title, resume_path, coverLetter }) => {
  const theme = useTheme();
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [showCertificatesModal, setShowCertificatesModal] = useState(false);
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [recruiter, setRecruiter] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const [showAllEducation, setShowAllEducation] = useState(false);
  const [showAllCertificates, setShowAllCertificates] = useState(false);
  const [showAllExperience, setShowAllExperience] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isEmailHistoryOpen, setIsEmailHistoryOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [isEmailPopupOpen, setIsEmailPopupOpen] = useState(false);

  if (!candidate) return null;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleResumeClick = async () => {
    console.log(resume_path);
    if (!resume_path) {
      alert("Resume  is not available");
      return;
    }

    const normalizedResumePath = resume_path.replace(/\\/g, "/");
    const fileURL = `${apiUrl}media/${normalizedResumePath}`;
    console.log('here is resume: ', fileURL);

    try {
      const response = await fetch(`${apiUrl}ai/search_resume/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ resume_url: fileURL }),
      });

      const contentType = response.headers.get("content-type");

      if (response.ok) {
        if (contentType && contentType.includes("application/pdf")) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank");
        } else {
          console.log("Unexpected response from server");
        }
      } else {
        if (contentType && contentType.includes("text/html")) {
          const htmlContent = await response.text();
          const newWindow = window.open();
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        } else {
          const data = await response.json();
          console.log(data.message);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const processLanguages = (languageData) => {
    if (!languageData) return [];

    if (Array.isArray(languageData)) {
      return languageData;
    }

    if (typeof languageData === 'string') {
      return languageData
        .replace(/^\[|\]$/g, '')
        .split(',')
        .map(lang => lang.trim().replace(/^['"]|['"]$/g, ''));
    }

    return [];
  };

  const processExperiences = (experienceData) => {
    if (!experienceData) return [];

    if (Array.isArray(experienceData)) {
      return experienceData;
    }

    if (typeof experienceData === 'string') {
      try {
        if (experienceData.startsWith('[') && experienceData.endsWith(']')) {
          const items = experienceData
            .substring(1, experienceData.length - 1)
            .split(/},\s*{/);

          return items.map((item, index) => {
            let itemStr = item;
            if (index > 0) itemStr = '{' + itemStr;
            if (index < items.length - 1) itemStr = itemStr + '}';

            const textMatch = itemStr.match(/'text':\s*'([^']*)'/);
            const dateMatch = itemStr.match(/'date_range':\s*(?:'([^']*)'|None)/);

            return {
              text: textMatch ? textMatch[1] : "No text available",
              date_range: dateMatch && dateMatch[1] ? dateMatch[1] : null
            };
          });
        }
      } catch (e) {
        console.error("Error parsing experiences:", e);
      }

      return [{
        text: experienceData,
        date_range: null
      }];
    }

    return [];
  };

  const openSkillsModal = () => {
    setShowSkillsModal(true);
  };

  const closeSkillsModal = () => {
    setShowSkillsModal(false);
  };

  const openEducationModal = () => {
    setShowEducationModal(true);
  };

  const closeEducationModal = () => {
    setShowEducationModal(false);
  };

  const openCertificatesModal = () => {
    setShowCertificatesModal(true);
  };

  const closeCertificatesModal = () => {
    setShowCertificatesModal(false);
  };

  const openExperienceModal = () => {
    setShowExperienceModal(true);
  };

  const closeExperienceModal = () => {
    setShowExperienceModal(false);
  };

  const searchProfile = async (name) => {
    try {
      const payload = {
        name: name
      }
      const response = await axios.post(`${apiUrl}authentication/linkedin/search-profile/`, payload);
      const url = response.data.search_url;
      console.log("LinkedIn Profile URL:", url);

      if (url) {
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.focus();
        }
      }

      return url;

    } catch (error) {
      console.error('Search profile failed:', error);
      alert(error.response?.data?.detail || 'Failed to search profile');
      throw error;
    }
  };

  const handleSendEmail = async (emailData) => {
    try {
      console.log("datadata: ", emailData);
      const { userId: userId, to: receiver, subject, body } = emailData;

      if (!receiver) {
        alert("Email receiver is empty");
        return;
      }

      if (!validateEmail(receiver)) {
        alert("Invalid email format");
        return;
      }

      const payload = {
        user_id: userId,
        to: receiver,
        subject,
        body,
      };
      console.log("Here is the send data:", payload);

      const response = await axios.post(
        `${apiUrl}authentication/api/send-email/`,
        payload
      );

      alert("Email sent successfully!");
      return response.data;
    } catch (error) {
      console.error("Email sending failed:", error);
      alert(error.response?.data?.detail || "Failed to send email");
      throw error;
    }
  };

  const handleEmailClick = (event) => {
    event.preventDefault();
    fetchTemplates();
    setIsTemplateModalOpen(true);
  };

  const handleEmailHistoryClick = (event) => {
    event.preventDefault();
    setIsEmailHistoryOpen(true);
  };

  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      console.log("Access token:", decodedToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  };

  const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    console.log("payload:", jsonPayload);

    return JSON.parse(jsonPayload);
  };

  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const recruiterId = getUserIdFromAccessToken();
      const response = await axios.get(
        `${apiUrl}recruiters/${recruiterId}/templates/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.data.results) {
        setTemplates(response.data.results);
      } else {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  return (
    <StyledCard>
      <Box position="relative">
        <Tooltip title="AI-generated match score" arrow placement="top-end">
          <Box>
            <ScoreTriangle score={candidate.score_matching} />
            <ScoreContent>
              <Tooltip
                title="This score represents the AI-evaluated match between the job description and the candidate's resume. It considers factors such as skills, experience, and qualifications to determine the overall fit for the position."
                arrow
                placement="bottom"
              >
                <InfoIcon size="small">
                  <InfoOutlinedIcon fontSize="small" />
                </InfoIcon>
              </Tooltip>
              <ScoreText>{candidate.score_matching}%</ScoreText>
              <ScoreComment>
                {getScoreComment(candidate.score_matching)}
              </ScoreComment>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <SmartToyIcon sx={{ fontSize: { xs: 12, sm: 14 }, mr: 0.5, color: "#fff" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#fff", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
                >
                  Évaluation de l'IA
                </Typography>
              </Box>
            </ScoreContent>
          </Box>
        </Tooltip>
        <CardContent sx={{
          p: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 4, sm: 5, md: 7 }
        }}>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="flex-start">
            {/* Left Column */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight="bold"
                  sx={{ mr: 1 }}
                >
                  {candidate.name}, {typeof candidate.age === 'number' ? candidate.age : 'N/A'}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 2,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: "bold"
                }}
              >
                {typeof candidate.profil === 'string' ? candidate.profil : Array.isArray(candidate.profil) ? candidate.profil.join(', ') : 'N/A'}
              </Typography>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                mb: { xs: 2, sm: 3, md: 4 }
              }}>
                <InfoChip
                  icon={<LocationOnIcon />}
                  label={candidate.phone || "phone"}
                  size="small"
                />
                <InfoChip
                  icon={<EmailIcon />}
                  label={candidate.email}
                  size="small"
                  onClick={() => (window.location.href = `mailto:${candidate.email}`)}
                  sx={{ cursor: 'pointer' }}
                />
                <InfoChip
                  icon={<LinkedInIcon />}
                  label={"See LinkedIn Profile"}
                  size="small"
                  onClick={() => searchProfile(candidate.name)}
                />
              </Box>
              {/* Education Display */}
              <Box sx={{ mt: { xs: 2, sm: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}
                >
                  <GraduationCap style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, marginRight: 12 }} />
                  Éducation
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {processLanguages(candidate?.education).length > 0 ? (
                    <>
                      {processLanguages(candidate?.education)
                        .slice(0, 2)
                        .map((education, index) => (
                          <Chip key={index} label={education} sx={{ mr: 1, mb: 1 }} />
                        ))}
                      {processLanguages(candidate?.education).length > 2 && (
                        <Button
                          variant="text"
                          color="primary"
                          onClick={openEducationModal}
                          size="small"
                          sx={{ textTransform: "none !important", fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {`...See More (${processLanguages(candidate?.education).length - 2})`}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Chip label="No Education listed" sx={{ mr: 1, mb: 1 }} />
                  )}
                </Box>
              </Box>

              {/* Education Modal */}
              <Dialog
                open={showEducationModal}
                onClose={closeEducationModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
              >
                <DialogTitle>
                  Tous niveaux d'enseignement
                  <IconButton
                    aria-label="close"
                    onClick={closeEducationModal}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {processLanguages(candidate?.education).map((education, index) => (
                      <Chip
                        key={index}
                        label={typeof education === 'string' ? education : JSON.stringify(education)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeEducationModal} color="primary" variant="contained">
                    Fermer
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Languages */}
              <Box sx={{ mb: 2, mt: { xs: 2, sm: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}
                >
                  <Language style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, marginRight: 12 }} />
                  Langues
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {processLanguages(candidate?.language).length > 0 ? (
                    processLanguages(candidate?.language).map((language, index) => (
                      <Chip
                        key={index}
                        label={typeof language === 'string' ? language : JSON.stringify(language)}
                        sx={{ mr: 1 }}
                      />
                    ))
                  ) : (
                    <Chip label="No Languages listed" sx={{ mr: 1, mb: 1 }} />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Middle Column */}
            <Grid item xs={12} md={4}>
              {/* Skills */}
              <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}
                >
                  <Checklist style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, marginRight: 12 }} />
                  COMPÉTENCES
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {processLanguages(candidate?.skills).length > 0 ? (
                    <>
                      {processLanguages(candidate?.skills)
                        .slice(0, showAllSkills ? undefined : 2)
                        .map((skill, index) => (
                          <Chip key={index} label={skill} sx={{ mr: 1, mb: 1 }} />
                        ))}
                      {processLanguages(candidate?.skills).length > 2 && (
                        <Button
                          variant="text"
                          color="primary"
                          onClick={openSkillsModal}
                          size="small"
                          sx={{ textTransform: "none !important", fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {showAllSkills
                            ? "Show Less"
                            : `...See More (${processLanguages(candidate?.skills).length - 2})`}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Chip label="No Skills listed" sx={{ mr: 1, mb: 1 }} />
                  )}
                </Box>
              </Box>

              {/* Skills Modal */}
              <Dialog
                open={showSkillsModal}
                onClose={closeSkillsModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
              >
                <DialogTitle>
                  Toutes les compétences
                  <IconButton
                    aria-label="close"
                    onClick={closeSkillsModal}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {processLanguages(candidate?.skills).map((skill, index) => (
                      <Chip
                        key={index}
                        label={typeof skill === 'string' ? skill : JSON.stringify(skill)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeSkillsModal} color="primary" variant="contained">
                    Fermer
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Experiences */}
              <Box sx={{ mb: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, sm: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}
                >
                  <WorkHistoryOutlined style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, marginRight: 12 }} />
                  Expérience Professionnelle
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    + &nbsp;<strong>{typeof candidate.agexperiece === 'number' ? Math.floor(candidate.agexperiece) : 'N/A'} years</strong>&nbsp;
                    {candidate?.experiences &&
                      candidate.experiences.length > 0 && (
                        <Button
                          variant="text"
                          color="primary"
                          onClick={openExperienceModal}
                          sx={{
                            ml: 1,
                            p: '2px 8px',
                            minHeight: 0,
                            lineHeight: 'normal',
                            textTransform: 'none',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}
                          size="small"
                        >
                          Voir plus
                        </Button>
                      )}
                  </Typography>
                </Box>
              </Box>

              {/* Experience Modal */}
              <Dialog
                open={showExperienceModal}
                onClose={closeExperienceModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
              >
                <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    component="div"
                    sx={{ fontWeight: 600, color: '#333' }}
                  >
                    Expérience Professionnelle
                  </Typography>
                  <IconButton
                    aria-label="close"
                    onClick={closeExperienceModal}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: '#666'
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ padding: 0 }}>
                  <Box sx={{ maxHeight: '70vh', overflow: 'auto', padding: { xs: 2, sm: 3 } }}>
                    {processExperiences(candidate?.experiences).map((experience, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: { xs: 1.5, sm: 2.5 },
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          backgroundColor: index % 2 === 0 ? '#f9fafb' : '#ffffff',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.08)'
                          }
                        }}
                      >
                        <Typography
                          variant="body1"
                          component="div"
                          sx={{
                            color: '#424242',
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            fontWeight: 500,
                            lineHeight: 1.6,
                            whiteSpace: 'pre-line'
                          }}
                        >
                          {experience.text}
                        </Typography>
                        {experience.date_range !== 'date_range not found' && (
                          <Typography
                            variant="body2"
                            component="div"
                            sx={{
                              color: '#757575',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              fontStyle: 'italic',
                              mt: 1
                            }}
                          >
                            {experience.date_range}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={closeExperienceModal}
                    color="primary"
                    variant="contained"
                    sx={{
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    Fermer
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Certificates */}
              <Box sx={{ mb: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, sm: 2, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}
                >
                  <WorkspacePremium style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, marginRight: 12 }} />
                  Certificats
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {processLanguages(candidate?.certificates).length > 0 ? (
                    <>
                      {processLanguages(candidate?.certificates)
                        .slice(0, showAllCertificates ? undefined : 1)
                        .map((skill, index) => (
                          <Chip key={index} label={skill} sx={{ mr: 1, mb: 1 }} />
                        ))}
                      {processLanguages(candidate?.certificates).length > 1 && (
                        <Button
                          variant="text"
                          color="primary"
                          onClick={openCertificatesModal}
                          size="small"
                          sx={{ textTransform: "none !important", fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {showAllCertificates
                            ? "Show Less"
                            : `...See More (${processLanguages(candidate?.certificates).length - 3})`}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Chip label="No Certificates listed" sx={{ mr: 1, mb: 1 }} />
                  )}
                </Box>
              </Box>

              {/* Certificates Modal */}
              <Dialog
                open={showCertificatesModal}
                onClose={closeCertificatesModal}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
              >
                <DialogTitle>
                  Tous les Certificats
                  <IconButton
                    aria-label="close"
                    onClick={closeCertificatesModal}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8
                    }}
                  >
                    <Close />
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {processLanguages(candidate?.certificates).map((skill, index) => (
                      <Chip
                        key={index}
                        label={typeof skill === 'string' ? skill : JSON.stringify(skill)}
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeCertificatesModal} color="primary" variant="contained">
                    Fermer
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={4} sx={{ mt: { xs: 2, sm: 2, md: 0 } }}>
              <Divider sx={{ my: { xs: 2, sm: 2, md: 3 } }} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  mb: { xs: 2, sm: 3, md: 4 },
                }}
              >
                <StyledTypography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                  }}
                >
                  Application Status
                </StyledTypography>
                <Chip
                  label='Candiate Imported'
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: { xs: "0.875rem", sm: "0.95rem", md: "1rem" },
                    padding: { xs: "15px 12px", sm: "18px 14px", md: "20px 15px" },
                    borderRadius: "25px",
                    fontWeight: "bold",
                    backgroundColor: COLORS.onHold_button,
                    color: COLORS.white,
                    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: { xs: 1.5, sm: 2 },
                mb: 2
              }}>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={handleResumeClick}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    fontFamily: "Poppins",
                    backgroundColor: '#0063cc',
                    borderColor: '#0063cc',
                    textTransform: "none",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    "&:hover": {
                      borderColor: '#0062cc',
                      boxShadow: 'none',
                    },
                    gridColumn: { xs: '1', sm: 'span 2' }
                  }}
                >
                  {t("resume")}
                </Button>

                <a
                  href={`mailto:${candidate.email}`}
                  style={{ textDecoration: "none", gridColumn: { xs: '1', sm: 'span 2' } }}
                >
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<EmailIcon />}
                    onClick={handleEmailClick}
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      fontFamily: "Poppins",
                      textTransform: "none",
                      mt: { xs: 0, sm: 1 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      gridColumn: { xs: '1', sm: 'span 2' }
                    }}
                  >
                    CONTACT
                  </Button>
                  {isTemplateModalOpen && (
                    <SendEmailModal
                      open={isTemplateModalOpen}
                      onClose={() => setIsTemplateModalOpen(false)}
                      template={null}
                      recruiterId={getUserIdFromAccessToken()}
                      theme={theme}
                      candidateInfo={{
                        email: candidate.email,
                        firstName: candidate.name.split(",")[0].split(" ")[0],
                        lastName: candidate.name
                          .split(",")[0]
                          .split(" ")
                          .slice(1)
                          .join(" "),
                        applicationId: candidate.id,
                      }}
                      showTemplateSelection={true}
                    />
                  )}
                </a>

                <Button
                  variant="contained"
                  startIcon={<HistoryIcon />}
                  onClick={handleEmailHistoryClick}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    fontFamily: "Poppins",
                    textTransform: "none",
                    mt: { xs: 0, sm: 1 },
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    backgroundColor: 'gray',
                    gridColumn: { xs: '1', sm: 'span 2' },
                    '&:hover': {
                      backgroundColor: '#666666'
                    }
                  }}
                >
                  EMAIL HISTORY
                </Button>

                <EmailHistory
                  open={isEmailHistoryOpen}
                  onClose={() => setIsEmailHistoryOpen(false)}
                  candidateEmail={candidate.email}
                  recruiterId={getUserIdFromAccessToken()}
                />
              </Box>

              <Box sx={{
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-end" },
                mt: { xs: 2, sm: 2, md: 3 }
              }}>
                <StyledTypography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                    textAlign: { xs: "center", sm: "right" },
                  }}
                >
                  {t("applied_on")}: {new Date(candidate.date).toLocaleDateString()}
                </StyledTypography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Box>
    </StyledCard>
  );
};

const CandidateCards = ({ candidates, post_title, post_id, filterOptions, setFilterOptions, onFilterApply, postQuiz, steps }) => {
  const { t } = useTranslation();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Post Quiz state (locally managed if prop is null)
  const [localPostQuiz, setLocalPostQuiz] = useState(postQuiz);

  useEffect(() => {
    if (postQuiz) {
      setLocalPostQuiz(postQuiz);
    }
  }, [postQuiz]);

  useEffect(() => {
    const fetchQuiz = async () => {
      if (!localPostQuiz && post_id && apiUrl) {
        try {
          const response = await axios.get(`${apiUrl}posts/post_quiz/${post_id}/`);
          if (response.data && response.data !== -1) {
            setLocalPostQuiz(response.data);
          }
        } catch (error) {
          console.error("Error fetching quiz data:", error);
        }
      }
    };
    fetchQuiz();
  }, [post_id, apiUrl, localPostQuiz]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCandidates, setFilteredCandidates] = useState(null);
  const displayCandidates = filteredCandidates ?? candidates;

  // Handle search and filters
  const handleSearch = () => {
    const query = searchQuery.toLowerCase().trim();

    // Check if we have any active filters
    const hasQuizFilters = filterOptions?.quiz_answers && Object.keys(filterOptions.quiz_answers).length > 0;
    const hasStepFilter = !!filterOptions?.step;
    const hasQuizCheckbox = !!filterOptions?.quiz;

    if (!query && !hasQuizFilters && !hasStepFilter && !hasQuizCheckbox) {
      setFilteredCandidates(null);
      return;
    }

    const filtered = candidates.filter((candidate) => {
      // 1. Name/Info search
      let nameMatch = true;
      if (query) {
        const firstName = candidate.candidate?.first_name?.toLowerCase() || "";
        const lastName = candidate.candidate?.last_name?.toLowerCase() || "";
        const fullName = `${firstName} ${lastName}`.trim();
        const extractedName = candidate.name?.toLowerCase() || "";
        const candidateEmail = candidate.email?.toLowerCase() || "";

        nameMatch = firstName.includes(query) ||
          lastName.includes(query) ||
          fullName.includes(query) ||
          extractedName.includes(query) ||
          candidateEmail.includes(query);
      }

      // 2. Step filter (if applicable for scraped candidates)
      let stepMatch = true;
      if (hasStepFilter) {
        stepMatch = (candidate.step || candidate.status) === filterOptions.step;
      }

      // 3. Quiz presence filter
      let quizPresenceMatch = true;
      if (hasQuizCheckbox) {
        quizPresenceMatch = !!(candidate.quiz && Object.keys(candidate.quiz).length > 0);
      }

      // 4. Specific Quiz answers filter
      let quizAnswersMatch = true;
      if (hasQuizFilters) {
        const candidateAnswers = candidate.quiz || {};
        const candidateName = candidate.name || (candidate.candidate ? `${candidate.candidate.first_name} ${candidate.candidate.last_name}` : "Unknown");

        quizAnswersMatch = Object.entries(filterOptions.quiz_answers).every(([qId, filterValue]) => {
          // If no filter for this question, it's a match
          if (filterValue === undefined || filterValue === null || filterValue === '' || (Array.isArray(filterValue) && filterValue.length === 0)) {
            return true;
          }

          let candidateValue = undefined;

          // Handle different quiz structures
          if (Array.isArray(candidateAnswers)) {
            // Structure: [ { question: qId, answer: value }, ... ]
            const response = candidateAnswers.find(r =>
              String(r.question) === String(qId) ||
              String(r.question) === `question_${qId}` ||
              `question_${r.question}` === String(qId)
            );
            candidateValue = response ? response.answer : undefined;
          } else if (typeof candidateAnswers === 'object' && candidateAnswers !== null) {
            // Structure: { qId: value, ... } or { question_qId: value, ... }
            if (candidateAnswers[qId] !== undefined) {
              candidateValue = candidateAnswers[qId];
            } else if (candidateAnswers[`question_${qId}`] !== undefined) {
              candidateValue = candidateAnswers[`question_${qId}`];
            } else if (String(qId).startsWith('question_')) {
              const rawId = String(qId).replace('question_', '');
              candidateValue = candidateAnswers[rawId];
            }
          }

          let isMatch = false;
          if (candidateValue === undefined) {
            isMatch = false;
          } else if (Array.isArray(filterValue)) {
            // Multi-choice filter: any selected filter must match the candidate's answer(s)
            const candidateValues = Array.isArray(candidateValue)
              ? candidateValue.map(v => String(v).toLowerCase())
              : [String(candidateValue).toLowerCase()];

            isMatch = filterValue.some(fv => candidateValues.includes(String(fv).toLowerCase()));
          } else {
            // Single-choice or text filter
            if (Array.isArray(candidateValue)) {
              // If candidate answered with multiple options (checkbox), check if any matches filter
              isMatch = candidateValue.some(cv => String(cv).toLowerCase().includes(String(filterValue).toLowerCase()));
            } else {
              // Case-insensitive partial match
              const candStr = String(candidateValue || "").toLowerCase();
              const filterStr = String(filterValue || "").toLowerCase();
              isMatch = candStr.includes(filterStr);
            }
          }

          if (!isMatch) {
            console.log(`❌ Scraped mismatch for ${candidateName}: Q ${qId}, Found: "${candidateValue}", Expected: "${filterValue}"`);
          } else {
            console.log(`✅ Scraped match for ${candidateName}: Q ${qId}, Found: "${candidateValue}", Expected: "${filterValue}"`);
          }

          return isMatch;
        });
      }

      return nameMatch && stepMatch && quizPresenceMatch && quizAnswersMatch;
    });

    setFilteredCandidates(filtered);
  };

  // Automatically apply filters when search query or filter options change
  useEffect(() => {
    handleSearch();
  }, [filterOptions, candidates, searchQuery]);

  return (
    <Box>
      {/* Search Bar Container - Similar to DynamicFormSearchBar */}
      <Box sx={{ p: 2 }}>
        <Grid container>
          <Grid item md={6} display={{ xs: "none", md: "block" }}>
            {/* Result count on the left */}
            {displayCandidates.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  pl: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#0017E7",
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    fontFamily: "Poppins",
                  }}
                >
                  {displayCandidates.length} {t("dynamicFormSearchBar.applicationsFound", "candidatures trouvées")}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {/* Search Box Container */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  backgroundColor: "#fff",
                  width: { xs: "100%", md: "100%" },
                  maxWidth: { xs: "100%", md: 716 },
                  height: { xs: "auto", md: 70 },
                  borderRadius: "20px",
                  border: "1px solid #e0e0e0",
                  padding: { xs: "8px", md: "10px" },
                  gap: { xs: "8px", md: "10px" },
                  alignItems: "center",
                  marginTop: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  mb: 2,
                }}
              >
                {/* Search input */}
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    flex: 1,
                  }}
                >
                  <PostSearchBar
                    variant="outlined"
                    size="small"
                    type="text"
                    placeholder={t("dynamicFormSearchBar.filterByName", "Filtrer par nom")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      flex: 1,
                      mr: { xs: 0, sm: 1 },
                      mb: { xs: 1, sm: 0 },
                      width: { xs: "100%", sm: "100%" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <img
                            src={searchIcon}
                            height={15}
                            width={15}
                            alt={t("dynamicFormSearchBar.search")}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Search and Filter Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row" },
                    width: { xs: "100%", sm: "auto" },
                    justifyContent: { xs: "space-between", sm: "flex-start" },
                    gap: 1,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{
                      textTransform: "none",
                      fontFamily: "Poppins",
                      width: { xs: "calc(100% - 60px)", sm: 106 },
                      height: { xs: 45, md: 50 },
                      borderRadius: "11px",
                      p: { xs: "10px", md: "14px 26px" },
                      gap: 10,
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      backgroundColor: "#0017E7",
                      "&:hover": {
                        backgroundColor: "rgba(0, 23, 231, 0.9)",
                      },
                    }}
                  >
                    {t("dynamicFormSearchBar.search", "Rechercher")}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => setFilterDialogOpen(true)}
                    sx={{
                      border: "1px solid #0017E7",
                      boxShadow: "none",
                      ":hover": {
                        backgroundColor: "#f0f4ff",
                        boxShadow: "0 2px 4px rgba(0, 23, 231, 0.2)",
                      },
                      transition: "all 0.3s ease",
                      borderRadius: "11px",
                      backgroundColor: "#fff",
                      fontFamily: "Poppins",
                      minWidth: "auto",
                      padding: "8px",
                      height: { xs: 45, md: 50 },
                      width: { xs: 50, md: 50 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: { xs: 1, sm: 1 },
                      "& .MuiSvgIcon-root": {
                        width: { xs: "18px", md: "20px" },
                        height: { xs: "18px", md: "20px" },
                        color: "#0017E7",
                      },
                    }}
                  >
                    <Filter />
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Typography
        sx={{
          fontFamily: "Poppins",
          fontSize: { xs: "0.875rem", sm: "1.3rem" },
          mb: { xs: 1, sm: 1.5, md: 2 },
          color: COLORS.primary,
          fontWeight: 500,
        }}
      >
        {displayCandidates.length} candidats existants.
      </Typography>
      {displayCandidates.length === 0 ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No candidates applied yet
          </Typography>
        </Box>
      ) : (
        displayCandidates.map((candidate, index) => (
          <CandidateCard
            key={index}
            candidate={candidate}
            post_title={post_title}
            resume_path={candidate.resume}
            coverLetter={candidate.cover_letter}
          />
        ))
      )}
      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        filters={filterOptions}
        setFilters={setFilterOptions}
        onApply={() => {
          setFilterDialogOpen(false);
          if (onFilterApply) onFilterApply();
        }}
        postQuiz={localPostQuiz}
        steps={steps}
      />
    </Box>
  );
};

export default CandidateCards;